/**
 * コントロールボタンの管理クラス
 */
export class ControlButtons {
  private runBtn: HTMLButtonElement
  private pauseBtn: HTMLButtonElement
  private stepBtn: HTMLButtonElement
  private resetBtn: HTMLButtonElement
  
  constructor() {
    this.runBtn = document.getElementById('run-btn') as HTMLButtonElement
    this.pauseBtn = document.getElementById('pause-btn') as HTMLButtonElement
    this.stepBtn = document.getElementById('step-btn') as HTMLButtonElement
    this.resetBtn = document.getElementById('reset-btn') as HTMLButtonElement
    
    this.setupAccessibility()
    this.setupTooltips()
  }
  
  /**
   * アクセシビリティ機能を設定
   */
  private setupAccessibility(): void {
    // キーボードショートカットを設定
    document.addEventListener('keydown', (e) => {
      // Ctrl/Cmd + Enter: 実行/一時停止
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        if (!this.runBtn.disabled) {
          this.runBtn.click()
        } else if (!this.pauseBtn.disabled) {
          this.pauseBtn.click()
        }
      }
      
      // Ctrl/Cmd + .: ステップ実行
      if ((e.ctrlKey || e.metaKey) && e.key === '.') {
        e.preventDefault()
        if (!this.stepBtn.disabled) {
          this.stepBtn.click()
        }
      }
      
      // Ctrl/Cmd + R: リセット
      if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
        e.preventDefault()
        if (!this.resetBtn.disabled) {
          this.resetBtn.click()
        }
      }
    })
  }
  
  /**
   * ツールチップを設定
   */
  private setupTooltips(): void {
    // ツールチップにショートカットを追加
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
    const modKey = isMac ? 'Cmd' : 'Ctrl'
    
    this.runBtn.title = `実行 (${modKey}+Enter)`
    this.pauseBtn.title = `一時停止 (${modKey}+Enter)`
    this.stepBtn.title = `ステップ実行 (${modKey}+.)`
    this.resetBtn.title = `リセット (${modKey}+R)`
  }
  
  /**
   * ボタンの状態を更新
   */
  updateState(state: 'idle' | 'running' | 'paused' | 'completed' | 'error'): void {
    // ボタンのアニメーション効果を追加
    this.removeAllPulseEffects()
    
    switch (state) {
      case 'idle':
        this.setButtonStates(false, true, false, false)
        this.runBtn.classList.add('pulse')
        break
      case 'running':
        this.setButtonStates(true, false, true, false)
        this.pauseBtn.classList.add('pulse')
        break
      case 'paused':
        this.setButtonStates(false, true, false, false)
        this.stepBtn.classList.add('pulse')
        break
      case 'completed':
        this.setButtonStates(false, true, false, false)
        this.showCompletionFeedback()
        break
      case 'error':
        this.setButtonStates(false, true, true, false)
        this.resetBtn.classList.add('pulse')
        break
    }
  }
  
  /**
   * ボタンの無効/有効状態を設定
   */
  private setButtonStates(
    runDisabled: boolean,
    pauseDisabled: boolean,
    stepDisabled: boolean,
    resetDisabled: boolean
  ): void {
    this.runBtn.disabled = runDisabled
    this.pauseBtn.disabled = pauseDisabled
    this.stepBtn.disabled = stepDisabled
    this.resetBtn.disabled = resetDisabled
    
    // aria-disabled属性も更新
    this.runBtn.setAttribute('aria-disabled', String(runDisabled))
    this.pauseBtn.setAttribute('aria-disabled', String(pauseDisabled))
    this.stepBtn.setAttribute('aria-disabled', String(stepDisabled))
    this.resetBtn.setAttribute('aria-disabled', String(resetDisabled))
  }
  
  /**
   * パルスエフェクトを削除
   */
  private removeAllPulseEffects(): void {
    [this.runBtn, this.pauseBtn, this.stepBtn, this.resetBtn].forEach(btn => {
      btn.classList.remove('pulse')
    })
  }
  
  /**
   * 実行完了のフィードバック
   */
  private showCompletionFeedback(): void {
    // 成功アニメーションを追加
    const successIcon = document.createElement('span')
    successIcon.className = 'success-check'
    successIcon.innerHTML = '✓'
    successIcon.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 24px;
      color: var(--secondary-color);
      pointer-events: none;
    `
    
    this.runBtn.style.position = 'relative'
    this.runBtn.appendChild(successIcon)
    
    setTimeout(() => {
      successIcon.remove()
    }, 1000)
  }
  
  /**
   * イベントリスナーを設定
   */
  setupEventListeners(handlers: {
    onRun: () => void
    onPause: () => void
    onStep: () => void
    onReset: () => void
  }): void {
    this.runBtn.addEventListener('click', () => {
      this.animateButtonClick(this.runBtn)
      handlers.onRun()
    })
    
    this.pauseBtn.addEventListener('click', () => {
      this.animateButtonClick(this.pauseBtn)
      handlers.onPause()
    })
    
    this.stepBtn.addEventListener('click', () => {
      this.animateButtonClick(this.stepBtn)
      handlers.onStep()
    })
    
    this.resetBtn.addEventListener('click', () => {
      this.animateButtonClick(this.resetBtn)
      handlers.onReset()
    })
  }
  
  /**
   * ボタンクリックのアニメーション
   */
  private animateButtonClick(button: HTMLButtonElement): void {
    button.style.transform = 'scale(0.95)'
    setTimeout(() => {
      button.style.transform = ''
    }, 100)
  }
}